import React, { useCallback, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IntegrationScopeBlock } from "./IntegrationScopeBlock";
import { useMeCore } from "../../../../cores/me";
import { useGmailCore } from "../../../../cores/gmail";
import { useOffice365Core } from "../../../../cores/office365";
import { useSmtpCore } from "../../../../cores/smtp";
import { useGoogleCalendarCore } from "../../../../cores/googleCalendar";
import { useGlobalCore } from "../../../../cores/globals";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import {
  SMTP_ME_BIND_MODAL,
} from "../../../../constants/modal.constant";
import SmtpLoginForm from "../Smtp/SmtpLoginForm";
import { GoogleButton } from "../Google/GoogleButton";
import GmailAuthButton from "../Gmail/GmailBindCard";
import Office365AuthButton from "../Office365/Office365BindCard";
import SmtpAuthButton from "../Smtp/SmtpBindCard";

type Provider = "Gmail" | "Office365" | "Smtp" | "GoogleCalendar";

interface Props {
  provider: Provider;
}

export default function ScopedIntegrationDetail({ provider }: Props) {
  const intl = useIntl();
  const { company, getMeSocialSettings, getMeUserSettings } = useMeCore();
  const { turnOnModal, turnOffModal } = useGlobalCore();

  const {
    settings: gmailCompany,
    userSettings: gmailPersonal,
    get: getGmail,
    login: loginGmail,
    unbind: unbindGmail,
  } = useGmailCore();

  const {
    settings: officeCompany,
    userSettings: officePersonal,
    get: getOffice365,
    login: loginOffice365,
    unbind: unbindOffice365,
  } = useOffice365Core();

  const {
    settings: smtpCompany,
    userSettings: smtpPersonal,
    get: getSmtp,
    unbind: unbindSmtp,
  } = useSmtpCore();

  const {
    userSettings: googleCalendarPersonal,
    get: getGoogleCalendar,
    login: loginGoogleCalendar,
    unbind: unbindGoogleCalendar,
  } = useGoogleCalendarCore();

  useEffect(() => {
    if (!company?.id) return;
    const id = String(company.id);
    if (provider === "Gmail") {
      getGmail(id);
      getGmail(id, true);
    }
    if (provider === "Office365") {
      getOffice365(id);
      getOffice365(id, true);
    }
    if (provider === "Smtp") {
      getSmtp(id);
      getSmtp(id, true);
    }
    if (provider === "GoogleCalendar") {
      getGoogleCalendar(id);
    }
  }, [company, provider, getGmail, getOffice365, getSmtp, getGoogleCalendar]);

  const bindPersonalGmail = useCallback(async () => {
    const loginUrl = await loginGmail(company!.id, true);
    if (loginUrl) window.open(loginUrl, "_self");
  }, [company, loginGmail]);

  const unbindPersonalGmail = useCallback(() => {
    unbindGmail(company!.id, true, () => getMeUserSettings());
  }, [company, unbindGmail, getMeUserSettings]);

  const bindPersonalOffice365 = useCallback(async () => {
    const loginUrl = await loginOffice365(company!.id, true);
    if (loginUrl) window.open(loginUrl, "_self");
  }, [company, loginOffice365]);

  const unbindPersonalOffice365 = useCallback(() => {
    unbindOffice365(company!.id, true, () => getMeUserSettings());
  }, [company, unbindOffice365, getMeUserSettings]);

  const bindPersonalSmtp = useCallback(() => {
    turnOnModal(
      SMTP_ME_BIND_MODAL,
      <SmtpLoginForm
        asUser={true}
        onSuccess={() => {
          turnOffModal(SMTP_ME_BIND_MODAL);
          getMeUserSettings();
        }}
      />,
      Position.LEFT
    );
  }, [turnOnModal, turnOffModal, getMeUserSettings]);

  const unbindPersonalSmtp = useCallback(() => {
    unbindSmtp(company!.id, true, () => getMeUserSettings());
  }, [company, unbindSmtp, getMeUserSettings]);

  const bindPersonalGoogleCalendar = useCallback(async () => {
    const loginUrl = await loginGoogleCalendar(company!.id);
    if (loginUrl) window.open(loginUrl, "_self");
  }, [company, loginGoogleCalendar]);

  const unbindPersonalGoogleCalendar = useCallback(() => {
    unbindGoogleCalendar(company!.id, () => getMeUserSettings());
  }, [company, unbindGoogleCalendar, getMeUserSettings]);

  const personalGmailActive = useMemo(
    () => Boolean(gmailPersonal?.email),
    [gmailPersonal]
  );
  const personalOfficeActive = useMemo(
    () => Boolean(officePersonal?.email),
    [officePersonal]
  );
  const personalSmtpActive = useMemo(
    () => Boolean(smtpPersonal?.email),
    [smtpPersonal]
  );
  const personalCalendarActive = useMemo(
    () => Boolean(googleCalendarPersonal?.email),
    [googleCalendarPersonal]
  );

  if (provider === "Gmail") {
    return (
      <div className="integration-scope-panel">
        <IntegrationScopeBlock
          scope="company"
          descriptionId="app.integrations.gmail.companyDesc"
          connectedEmail={gmailCompany?.email}
          connectedName={gmailCompany?.name}
          connectedAvatar={gmailCompany?.avatarUrl}
        >
          <GmailAuthButton />
        </IntegrationScopeBlock>
        <IntegrationScopeBlock
          scope="personal"
          descriptionId="app.integrations.gmail.personalDesc"
          connectedEmail={gmailPersonal?.email}
          connectedName={gmailPersonal?.name}
          connectedAvatar={gmailPersonal?.avatarUrl}
        >
          <GoogleButton
            isActive={personalGmailActive}
            onClick={personalGmailActive ? unbindPersonalGmail : bindPersonalGmail}
          />
        </IntegrationScopeBlock>
      </div>
    );
  }

  if (provider === "Office365") {
    return (
      <div className="integration-scope-panel">
        <IntegrationScopeBlock
          scope="company"
          descriptionId="app.integrations.office365.companyDesc"
          connectedEmail={officeCompany?.email}
          connectedName={officeCompany?.name}
          connectedAvatar={officeCompany?.avatarUrl}
        >
          <Office365AuthButton />
        </IntegrationScopeBlock>
        <IntegrationScopeBlock
          scope="personal"
          descriptionId="app.integrations.office365.personalDesc"
          connectedEmail={officePersonal?.email}
          connectedName={officePersonal?.name}
          connectedAvatar={officePersonal?.avatarUrl}
        >
          <Button
            className="w-100"
            color="primary"
            size="sm"
            outline={personalOfficeActive}
            onClick={
              personalOfficeActive ? unbindPersonalOffice365 : bindPersonalOffice365
            }
          >
            {!personalOfficeActive
              ? intl.formatMessage({ id: "app.home.syncUp" })
              : intl.formatMessage({ id: "app.home.disconnect" })}
          </Button>
        </IntegrationScopeBlock>
      </div>
    );
  }

  if (provider === "Smtp") {
    return (
      <div className="integration-scope-panel">
        <IntegrationScopeBlock
          scope="company"
          descriptionId="app.integrations.smtp.companyDesc"
          connectedEmail={smtpCompany?.email}
        >
          <SmtpAuthButton />
        </IntegrationScopeBlock>
        <IntegrationScopeBlock
          scope="personal"
          descriptionId="app.integrations.smtp.personalDesc"
          connectedEmail={smtpPersonal?.email}
        >
          <Button
            className="w-100"
            color="primary"
            size="sm"
            outline={personalSmtpActive}
            onClick={personalSmtpActive ? unbindPersonalSmtp : bindPersonalSmtp}
          >
            {!personalSmtpActive
              ? intl.formatMessage({ id: "app.home.syncUp" })
              : intl.formatMessage({ id: "app.home.disconnect" })}
          </Button>
        </IntegrationScopeBlock>
      </div>
    );
  }

  return (
    <div className="integration-scope-panel">
      <IntegrationScopeBlock
        scope="company"
        descriptionId="app.integrations.googleCalendar.companyDesc"
      >
        <div className="integration-scope-block__note">
          <FontAwesomeIcon icon={["fas", "info-circle"]} className="mr-2" />
          {intl.formatMessage({ id: "app.integrations.googleCalendar.companyNote" })}
        </div>
      </IntegrationScopeBlock>
      <IntegrationScopeBlock
        scope="personal"
        descriptionId="app.integrations.googleCalendar.personalDesc"
        connectedEmail={googleCalendarPersonal?.email}
        connectedName={googleCalendarPersonal?.name}
        connectedAvatar={googleCalendarPersonal?.avatarUrl}
      >
        <GoogleButton
          onlySyncText
          isActive={personalCalendarActive}
          onClick={
            personalCalendarActive
              ? unbindPersonalGoogleCalendar
              : bindPersonalGoogleCalendar
          }
        />
      </IntegrationScopeBlock>
    </div>
  );
}
